import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../shared/Layout';
import PageHeader from '../shared/PageHeader';
import FilterBar from '../../components/settings/FilterBar';
import Icon from '../../components/tables/Icon';
import IconAdd from '../../components/icons/IconAdd';
import GroupModal from './GroupModal';
import language from '../../variables/language';
import '../../css/groups.css';

const Groups = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [studentCounts, setStudentCounts] = useState({});
  const [search, setSearch] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [modalState, setModalState] = useState('none');
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    axios.get('/api/groups', { withCredentials: true })
      .then(response => setGroups(response.data || []))
      .catch(err => {
        if (err.response && err.response.status === 401) navigate('/login');
      });
    axios.get('/api/students', { withCredentials: true })
      .then(response => {
        const counts = {};
        (response.data || []).forEach(student => {
          if (student.groupe && student.groupe.id) {
            counts[student.groupe.id] = (counts[student.groupe.id] || 0) + 1;
          }
        });
        setStudentCounts(counts);
      })
      .catch(() => {});
  }, [refresh, navigate]);

  const visibleGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query
      ? groups.filter(group => (group.name || '').toLowerCase().includes(query))
      : groups;
    return [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [groups, search]);

  const refreshList = () => setRefresh(prev => prev + 1);

  const openCreate = () => { setEditId(null); setModalState('create'); };
  const stateHundler = (state, id) => {
    if (state === 'edit') {
      setEditId(id);
      setModalState('edit');
    }
  };
  const closeModal = () => setModalState('none');
  const onSaved = () => { closeModal(); refreshList(); };

  return (
    <Layout>
      <PageHeader name={language[language.lang]['groups']}>
        <FilterBar
          search={{ value: search, onChange: setSearch, placeholder: language[language.lang].placeholderSearch }}
          resultCount={visibleGroups.length}
        />
        <div className="groups-header-actions">
          <div className="groups-add-btn" onClick={openCreate}>
            <IconAdd />
          </div>
        </div>
      </PageHeader>
      <main className="main-block">
        <div className="main-content txt-c groups-page">
          <table className="table groups-table">
            <thead className="table-header">
              <tr className="table-h-tr">
                <th className="table-h-th">{language[language.lang].name}</th>
                <th className="table-h-th">{language[language.lang].groupsPage.membersColumn}</th>
                <th className="table-h-th"></th>
              </tr>
            </thead>
            <tbody className="table-body">
              {visibleGroups.map(group => (
                <tr key={group.id} className="table-b-tr groups-row" onClick={() => navigate('/groups/' + group.id)}>
                  <td className="table-b-td groups-name-cell">{group.name}</td>
                  <td className="table-b-td">
                    <span className="groups-member-badge">{studentCounts[group.id] || 0}</span>
                  </td>
                  <td className="table-b-td groups-actions-cell" onClick={e => e.stopPropagation()}>
                    <Icon mode="edit" id={group.id} stateHundler={stateHundler}
                      className="icon-edit" classes="icon-dimensions-big" />
                    <Icon mode="delete" url={'/api/groups/' + group.id} onRefreshHundler={refreshList}
                      className="icon-delete" classes="icon-dimensions-big" />
                  </td>
                </tr>
              ))}
              {visibleGroups.length === 0 ? (
                <tr className="groups-empty-row">
                  <td colSpan={3}>{language[language.lang]['Empty list']}</td>
                </tr>
              ) : ''}
            </tbody>
          </table>
        </div>
      </main>
      <GroupModal modalState={modalState} groupId={editId} onClose={closeModal} onSaved={onSaved} />
    </Layout>
  );
};

export default Groups;
